import { useCallback, useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useLocalization } from '@/hooks/useLocalization';
import { useUserRole } from '@/hooks/useUserRole';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Same role list as the "Review roles can update strategy items" policy
// (migration 100) — anyone else gets a read-only list.
const REVIEW_ROLES = ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'executive', 'admin'];

interface ImportedInitiative {
  id: string;
  name: string;
  status: string;
  budget: number | null;
  start_date: string | null;
  end_date: string | null;
  portfolio_tag: string | null;
}

interface PillarOption {
  id: string;
  name: string;
}

interface ImportedInitiativesListProps {
  /** Bump to re-fetch (e.g. after an import is approved or undone). */
  refreshKey?: number;
  /** Called after an initiative is assigned to a pillar. */
  onAssigned?: () => void;
}

const formatDate = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

// Initiatives approved from documents have no pillar yet, so the pillar
// cards on the dashboard don't count them. This list is where they show
// up until a reviewer assigns one. Renders nothing when there are none.
export const ImportedInitiativesList = ({ refreshKey = 0, onAssigned }: ImportedInitiativesListProps) => {
  const { t, tx, currency } = useLocalization();
  const { role } = useUserRole();
  const canAssign = REVIEW_ROLES.includes(role || '');

  const [items, setItems] = useState<ImportedInitiative[]>([]);
  const [pillars, setPillars] = useState<PillarOption[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [itemsRes, pillarsRes] = await Promise.all([
      supabase
        .from('strategy_items')
        .select('id, name, status, budget, start_date, end_date, portfolio_tag')
        .is('pillar_id', null)
        .order('created_at', { ascending: false }),
      supabase.from('strategy_pillars').select('id, name').order('display_order'),
    ]);
    if (itemsRes.error) {
      console.error('Error fetching imported initiatives:', itemsRes.error);
      return;
    }
    setItems((itemsRes.data ?? []) as ImportedInitiative[]);
    if (!pillarsRes.error) setPillars((pillarsRes.data ?? []) as PillarOption[]);
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const assignPillar = async (itemId: string, pillarId: string) => {
    setSavingId(itemId);
    const { error } = await supabase.from('strategy_items').update({ pillar_id: pillarId }).eq('id', itemId);
    setSavingId(null);
    if (error) {
      toast.error(t('Could not assign the pillar'));
      return;
    }
    toast.success(t('Initiative assigned to pillar'));
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    onAssigned?.();
  };

  if (items.length === 0) return null;

  return (
    <div className="masira-card">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('Imported initiatives')}</h3>
        <p className="text-sm text-muted-foreground">
          {canAssign && pillars.length > 0
            ? t('From your documents — assign a pillar to count them in the dashboard')
            : t('From your documents — not yet assigned to a pillar')}
        </p>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50"
          >
            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-[12rem]">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{item.name}</span>
                <span
                  className={cn(
                    'masira-badge',
                    item.status === 'completed' && 'masira-badge-success',
                    item.status === 'active' && 'masira-badge-warning',
                  )}
                >
                  {tx(item.status)}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                {item.budget != null && <span>{currency(item.budget)}</span>}
                {(item.start_date || item.end_date) && (
                  <span>
                    {item.start_date ? formatDate(item.start_date) : '…'} – {item.end_date ? formatDate(item.end_date) : '…'}
                  </span>
                )}
                {item.portfolio_tag && <span>{item.portfolio_tag}</span>}
              </div>
            </div>
            {canAssign && pillars.length > 0 && (
              <Select disabled={savingId === item.id} onValueChange={(pillarId) => assignPillar(item.id, pillarId)}>
                <SelectTrigger className="w-48 h-8 text-xs">
                  <SelectValue placeholder={t('Assign pillar')} />
                </SelectTrigger>
                <SelectContent>
                  {pillars.map((pillar) => (
                    <SelectItem key={pillar.id} value={pillar.id}>
                      {pillar.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
